import { contentRoleOptions, getContentRoleLabel } from '@/lib/constants/contentRoles';
import { useCreateHandleiding, useDeleteHandleiding, useGetManagementHandleidingen, useUpdateHandleiding } from '@/lib/queries/content';
import { Handleiding } from '@/lib/types/content';
import { Roles } from '@/lib/context';
import { DeleteOutlined, EditOutlined, InboxOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Divider, Form, Input, message, Modal, Spin, Switch, Table, Tooltip } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import { UploadFile } from 'antd/es/upload/interface';
import { useState } from 'react';

export const GuideManagementPage: React.FunctionComponent = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm<Handleiding>();
  const [editingHandleiding, setEditingHandleiding] = useState<Handleiding | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { mutate, isPending: isSavingNewHandleiding } = useCreateHandleiding();
  const { mutate: deleteHandleiding } = useDeleteHandleiding();
  const { mutate: updateHandleiding, isPending: isUpdatingHandleiding } = useUpdateHandleiding();
  const { data: pdfList, isLoading, refetch } = useGetManagementHandleidingen();
  const columns = [
    {
      title: 'Naam',
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: 'Datum toegevoegd',
      key: 'datum',
      dataIndex: 'datum',
      render: (datum: string) => new Date(datum).toLocaleString(),
    },
    {
      title: 'Publiek',
      key: 'isPubliek',
      dataIndex: 'isPubliek',
      render: (value: boolean) => (value ? 'Ja' : 'Nee'),
    },
    {
      title: 'Doelgroepen',
      key: 'rollen',
      dataIndex: 'rollen',
      render: (rollen: Roles[]) => rollen?.map(getContentRoleLabel).join(', ') || '-',
    },
    {
      title: 'Versie',
      key: 'versienummer',
      dataIndex: 'versienummer',
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_: unknown, record: Handleiding) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Bewerken">
            <Button aria-label="Bewerken" type="text" icon={<EditOutlined style={{ color: '#1677ff' }} />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Verwijderen">
            <Button aria-label="Verwijderen" type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleEdit = (handleiding: Handleiding) => {
    setEditingHandleiding(handleiding);
    editForm.setFieldsValue({
      ...handleiding,
      rollen: handleiding.isPubliek ? [] : handleiding.rollen,
    });
  };

  const handleSaveEdit = async () => {
    const values = await editForm.validateFields();
    if (!editingHandleiding) return;

    updateHandleiding(
      {
        ...editingHandleiding,
        name: values.name,
        omschrijving: values.omschrijving,
        isPubliek: values.isPubliek,
        rollen: values.isPubliek ? [] : values.rollen,
        versienummer: values.versienummer,
      },
      {
        onSuccess: () => {
          setEditingHandleiding(null);
          refetch();
        },
      }
    );
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Weet je zeker dat je deze handleiding wilt verwijderen?',
      okText: 'Ja, verwijderen',
      okType: 'danger',
      cancelText: 'Annuleren',
      onOk: () => deleteHandleiding(id, { onSuccess: () => refetch() }),
    });
  };

  const handleSaveUpload = async () => {
    if (!selectedFile) {
      message.error('Selecteer eerst een PDF-bestand');
      return;
    }

    const values = await form.validateFields();
    const opgegevenNaam = values.name?.trim();
    const opslagNaam = opgegevenNaam?.toLowerCase().endsWith('.pdf') ? opgegevenNaam : `${opgegevenNaam}.pdf`;

    if (!opgegevenNaam) {
      message.error('Naam is verplicht');
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      let base64 = reader.result as string;
      base64 = base64.split(',')[1];

      mutate(
        {
          name: opslagNaam,
          file: base64,
          datum: new Date(),
          omschrijving: values.omschrijving,
          isPubliek: values.isPubliek ?? true,
          rollen: values.isPubliek ? [] : values.rollen,
          versienummer: values.versienummer,
        },
        {
          onSuccess: () => {
            refetch();
            setSelectedFile(null);
            setFileList([]);
            form.resetFields();
            message.success(`${selectedFile.name} uploaded successfully`);
          },
          onError: () => message.error('Failed to upload'),
        }
      );
    };

    reader.readAsDataURL(selectedFile);
  };

  const props = {
    name: 'file',
    multiple: false,
    beforeUpload: (file: File) => {
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        message.error('Alleen PDF-bestanden zijn toegestaan');
        return false;
      }

      setSelectedFile(file);
      setFileList([{ uid: file.name, name: file.name, status: 'done' }]);

      return false;
    },
    onRemove: () => {
      setSelectedFile(null);
      setFileList([]);
    },
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Form form={form} layout="vertical" initialValues={{ isPubliek: true, rollen: [] }} style={{ width: '100%' }}>
          <Col span={12}>
            <Form.Item name="name" label="Naam" rules={[{ required: true, message: 'Naam is verplicht' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="versienummer" label="Versienummer">
              <Input />
            </Form.Item>
            <Form.Item name="omschrijving" label="Omschrijving">
              <Input.TextArea />
            </Form.Item>
            <Form.Item name="isPubliek" label="Publiek zichtbaar" valuePropName="checked">
              <Switch checkedChildren="Ja" unCheckedChildren="Nee" onChange={checked => checked && form.setFieldValue('rollen', [])} />
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
              {() =>
                !form.getFieldValue('isPubliek') && (
                  <Form.Item name="rollen" label="Doelgroepen" rules={[{ required: true, message: '' }]}>
                    <Checkbox.Group options={contentRoleOptions} />
                  </Form.Item>
                )
              }
            </Form.Item>
          </Col>
          <Dragger {...props} fileList={fileList} disabled={isLoading || isSavingNewHandleiding}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Klik of sleep PDF naar dit gebied om te uploaden</p>
          </Dragger>
          {isSavingNewHandleiding && (
            <div style={{ marginTop: 12, marginBottom: 4, display: 'flex', justifyContent: 'center' }}>
              <Spin size="small" />
            </div>
          )}
          <Button
            type="primary"
            onClick={handleSaveUpload}
            loading={isSavingNewHandleiding}
            disabled={isLoading || !selectedFile || isSavingNewHandleiding}
            style={{ marginTop: 16 }}>
            Opslaan
          </Button>
        </Form>
      </div>
      <Divider />
      {isLoading ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table dataSource={pdfList} columns={columns} rowKey={record => record.id} />
      )}
      <Modal
        title="Handleiding bewerken"
        open={!!editingHandleiding}
        confirmLoading={isUpdatingHandleiding}
        okText="Opslaan"
        cancelText="Annuleren"
        onOk={handleSaveEdit}
        onCancel={() => setEditingHandleiding(null)}>
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label="Naam" rules={[{ required: true, message: '' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="omschrijving" label="Omschrijving">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="versienummer" label="Versienummer">
            <Input />
          </Form.Item>
          <Form.Item name="isPubliek" label="Publiek zichtbaar" valuePropName="checked">
            <Switch checkedChildren="Ja" unCheckedChildren="Nee" onChange={checked => checked && editForm.setFieldValue('rollen', [])} />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() =>
              !editForm.getFieldValue('isPubliek') && (
                <Form.Item name="rollen" label="Doelgroepen" rules={[{ required: true, message: '' }]}>
                  <Checkbox.Group options={contentRoleOptions} />
                </Form.Item>
              )
            }
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
